import { useEffect, useRef } from 'react';

import { useQuery, useQueryClient } from '@tanstack/react-query';

import { assetKeys } from '@/queries/assets';
import { checkKeys } from '@/queries/checks';
import { deploymentRuleKeys } from '@/queries/deployments';
import { formKeys } from '@/queries/forms';
import { scheduleFormKeys, scheduleKeys } from '@/queries/schedule';
import { ApiService } from '@/services/api-v2';

const POLL_INTERVAL_MS = 1500;

/**
 * Polls a background deployment-recalc run until it reaches a terminal state.
 * On completion, invalidates every register the recalc feeds (assets, checks,
 * schedules, forms) so deployed rows and new checks appear without a refresh.
 */
export function useDeploymentRecalcRun(runId: string | undefined) {
  const queryClient = useQueryClient();

  const { data: run } = useQuery({
    queryKey: deploymentRuleKeys.recalcRun(runId),
    queryFn: async () => {
      if (!runId) throw new Error('Recalc run ID is required');
      return await (await ApiService.GetDeploymentRecalcRun(runId)).json();
    },
    enabled: !!runId,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === 'completed' || status === 'failed' ? false : POLL_INTERVAL_MS;
    },
  });

  const status = run?.status;
  // Keyed by runId (not a one-shot boolean) so a second run polled by the same
  // mounted hook instance still triggers its own invalidation sweep.
  const invalidatedRunIdRef = useRef<string | undefined>(undefined);
  useEffect(() => {
    if (status !== 'completed' || !runId || invalidatedRunIdRef.current === runId) return;
    invalidatedRunIdRef.current = runId;
    void queryClient.invalidateQueries({ queryKey: deploymentRuleKeys.all });
    void queryClient.invalidateQueries({ queryKey: assetKeys.all });
    void queryClient.invalidateQueries({ queryKey: checkKeys.all });
    void queryClient.invalidateQueries({ queryKey: scheduleKeys.all });
    void queryClient.invalidateQueries({ queryKey: scheduleFormKeys.all });
    void queryClient.invalidateQueries({ queryKey: formKeys.all });
  }, [status, runId, queryClient]);

  return {
    isRecalculating: !!runId && status !== 'completed' && status !== 'failed',
    failed: status === 'failed',
    progress: run?.progress ?? 0,
  };
}
